import React from 'react';
import styled, { css } from 'styled-components';
import { useLocation } from '@reach/router';

import * as RadixMenu from '@radix-ui/react-navigation-menu';

import { color, media, theme, variants } from 'styles/utils';

import Button from 'components/Button';
import Icon from 'components/Icon';
import VerticalNavItem from './VerticalNavItem';
import HorizontalNavItem from './HorizontalNavItem';

export default function NavMenu(props) {
  return (
    <>
      <VerticalNavMenu {...props} />
      <HorizontalMenu {...props} />
    </>
  );
}

const StyledNav = styled.nav`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledVerticalNavMenu = styled(StyledNav)`
  ${media.min('nav')} {
    display: none;
  }

  .nav-menu-list {
    display: flex;
    flex-direction: column;
    align-items: start;
  }

  .nav-menu-list-cta {
    align-items: stretch;
  }

  .nav-menu-list[data-depth='1'] {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: [icon] max-content [content] 1fr;
    padding-left: 1.5rem;

    & > li,
    & > li > * {
      grid-column: 1/-1;
      display: grid;
      row-gap: 0.25rem;
      grid-template-columns: subgrid;
    }
  }

  .nav-menu-item[data-depth='1'] svg {
    grid-column: icon;
    font-size: 1.25em;
  }

  .nav-menu-item[data-depth='1'] :where(span, small),
  .nav-menu-list[data-depth='1'] .nav-menu-list {
    grid-column: content;
  }

  .nav-menu-item[data-depth='2'] {
    display: inline-flex;
    background: ${color('darkTeal')};
    font-size: 0.75rem;
    padding: 0.25rem;
  }

  ${variants({
    primary: css`
      background-color: ${color('mediumTeal')};

      .nav-menu-list {
        gap: 1rem;
      }

      .nav-menu-list[data-depth='0'] {
        padding: 1.5rem 3rem;
      }

      .nav-menu-list-cta {
        background: ${color('darkTeal')};
        height: 100%;
        padding-block: 2.5rem;
        row-gap: 1rem;

        .nav-menu-item {
          display: flex;
        }
      }

      .nav-menu-item[aria-current='page'][data-depth='0'] {
        position: relative;
        &::after {
          background-color: ${color('teal')};
          bottom: 0.5rem;
          content: '';
          height: 1px;
          left: 1rem;
          position: absolute;
          right: 1rem;
        }
      }
    `,
    secondary: css`
      background-color: ${color('white')};

      .nav-menu-list {
        align-items: stretch;
      }

      .nav-menu-item {
        display: block;
        border-top: 1px solid ${color('gray.50')};
        padding: 1rem;
      }
    `,
  })}
`;

function VerticalNavMenu(props) {
  return (
    <StyledVerticalNavMenu aria-label={props.label} variant={props.variant}>
      {props.items && renderNavList({ items: props.items })}
      {props.cta && renderNavList({ cta: true, items: props.cta })}
    </StyledVerticalNavMenu>
  );

  function renderNavList({ items, cta, depth = 0 }) {
    return (
      <ul
        className={`nav-menu-list ${cta ? 'nav-menu-list-cta' : ''}`}
        data-depth={depth}
        role="list"
      >
        {items.map((item) => (
          <li key={`${item.label}${item.href}`}>
            <VerticalNavItem depth={depth} {...item} />
            {item.items &&
              renderNavList({ items: item.items, depth: depth + 1 })}
          </li>
        ))}
      </ul>
    );
  }
}

const StyledHorizontalNavMenu = styled(StyledNav)`
  position: relative;

  ${media.max('nav')} {
    display: none;
  }

  .nav-menu-container {
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  .nav-menu-list {
    display: flex;
    flex-direction: column;
    column-gap: 0.5rem;
  }

  .nav-menu-item {
    position: relative;
  }

  .nav-menu-item small {
    opacity: 0.8;
    font-size: 0.625;
  }

  .nav-menu-container > .nav-menu-list {
    align-items: center;
    flex-direction: row;

    & > li {
      position: relative;
    }
  }

  .nav-menu-popover {
    filter: drop-shadow(0 0.25rem 1.5rem rgb(175 214 236 / 12%));
    position: absolute;
    top: 100%;
    left: 0;
    width: min-content;
    z-index: ${theme('zIndex.popover')};
  }

  .nav-menu-popover > .nav-menu-list {
    background: ${color('darkestTeal')};
    border-radius: 0.5rem;
    display: grid;
    gap: 0.75rem;
    grid-template-columns: [icon] max-content [content] 1fr;
    inline-size: max(28rem, max-content);
    margin-top: 0.25rem;
    padding: 1.125rem 0.75rem;
    position: relative;

    &::before {
      background: ${color('darkestTeal')};
      background-image: linear-gradient(
        to bottom right,
        ${color('teal')},
        transparent
      );
      border-radius: 0.625rem;
      content: '';
      inset: -1px;
      position: absolute;
      z-index: -1;
    }
  }

  .nav-menu-item[data-depth='0'] {
    display: flex;
    column-gap: 0.75em;
    justify-content: start;
    &:hover:not([data-variant='primary']) {
      text-decoration: underline;
      text-underline-offset: 0.3em;
    }
  }

  .nav-menu-item[data-depth='1'] {
    &:hover {
      background-color: ${color('darkTeal')};
    }
  }

  .nav-menu-list[data-depth='1'] > li,
  .nav-menu-list[data-depth='1'] > li > * {
    grid-column: 1/-1;
    display: grid;
    row-gap: 0.25rem;
    grid-template-columns: subgrid;
  }

  .nav-menu-item[data-depth='1'] svg {
    grid-column: icon;
    font-size: 1.25em;
  }

  .nav-menu-item[data-depth='1'] :where(span, small),
  .nav-menu-list[data-depth='1'] .nav-menu-list {
    grid-column: content;
  }

  .nav-menu-item[data-depth='2'] {
    display: inline-flex;
    font-size: 0.75rem;
    padding: 0.25rem;
    &:hover {
      background-color: ${color('darkTeal')};
    }
  }

  ${variants({
    primary: css`
      .nav-menu-item[aria-current='page'][data-depth='0']:not(
          [data-variant='primary']
        )::after {
        background-color: ${color('teal')};
        bottom: 0.65rem;
        content: '';
        height: 1px;
        left: 0.5rem;
        position: absolute;
        right: 0.5rem;
      }
    `,
  })}
`;

function HorizontalMenu(props) {
  const { pathname } = useLocation();

  return (
    <RadixMenu.Root asChild>
      <StyledHorizontalNavMenu aria-label={props.label} variant={props.variant}>
        <RadixMenu.List asChild>
          <div className="nav-menu-container">
            {props.items && renderNavList({ items: props.items })}
            {props.cta && renderNavList({ items: props.cta })}
            <RadixMenu.Indicator className="nav-menu-indicator" />
          </div>
        </RadixMenu.List>
      </StyledHorizontalNavMenu>
    </RadixMenu.Root>
  );

  function renderNavItem({
    depth,
    description,
    href,
    icon,
    items,
    label,
    variant = 'navItem',
  }) {
    if (items && depth === 0) {
      return (
        <RadixMenu.Item key={`${label}${href}`}>
          <RadixMenu.Trigger asChild>
            <Button
              className="nav-menu-item"
              data-depth={depth}
              size="sm"
              variant={variant}
            >
              {icon && <Icon name={icon} />}
              <span>{label}</span>
            </Button>
          </RadixMenu.Trigger>
          <RadixMenu.Content className="nav-menu-popover">
            {renderNavList({ depth: depth + 1, items })}
          </RadixMenu.Content>
        </RadixMenu.Item>
      );
    }
    return (
      <RadixMenu.Item key={`${label}${href}`}>
        <RadixMenu.Link asChild active={href === pathname}>
          <HorizontalNavItem
            depth={depth}
            description={description}
            href={href}
            icon={icon}
            label={label}
            variant={variant}
          />
        </RadixMenu.Link>
        {items && (
          <RadixMenu.Sub>
            {renderNavList({ depth: depth + 1, items })}
          </RadixMenu.Sub>
        )}
      </RadixMenu.Item>
    );
  }

  function renderNavList({ items, depth = 0 }) {
    return (
      <ul className="nav-menu-list" data-depth={depth} role="list">
        {items.map((item) => renderNavItem({ depth, ...item }))}
      </ul>
    );
  }
}
